import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/shared/utils/apiErrorMessage';
import { useMutation, useQuery } from '@tanstack/react-query';
import Editor from '@monaco-editor/react';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Switch } from '@/shared/components/ui/switch';
import { Button } from '@/shared/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { useToast } from '@/shared/hooks/use-toast';
import { useTheme } from '@/shared/hooks/use-theme';
import { EmailTemplateService } from '../services/emailTemplateService';
import { EmailSenderService } from '../services/emailSenderService';
import type { EmailTemplateBase } from '../types/EmailTemplateTypes';

type Lang = 'en' | 'pt';

const LANG_TO_LOCALE: Record<Lang, string[]> = {
  en: ['en-US', 'en'],
  pt: ['pt-BR', 'pt'],
};

/** Replaces {{userName}} so the preview reflects how a recipient sees it. */
function previewHtml(html: string): string {
  return html.replace(/\{\{userName\}\}/g, 'San Thiago');
}

export const EmailSenderSettings = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { theme } = useTheme();

  const [useTemplate, setUseTemplate] = useState(true);
  const [selectedSlug, setSelectedSlug] = useState<string>('');
  const [subjectEn, setSubjectEn] = useState('');
  const [subjectPt, setSubjectPt] = useState('');
  const [bodyEn, setBodyEn] = useState('');
  const [bodyPt, setBodyPt] = useState('');
  const [activeLang, setActiveLang] = useState<Lang>('en');
  const [view, setView] = useState<'code' | 'preview'>('code');
  const [testEmail, setTestEmail] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data: baseTemplates, isLoading } = useQuery({
    queryKey: ['email-templates', 'base'],
    queryFn: () => EmailTemplateService.getBaseTemplates(),
  });

  // One option per distinct slug (the dropdown picks a slug; both language
  // variants are loaded into the bilingual editor).
  const slugOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const tpl of baseTemplates ?? []) {
      if (!map.has(tpl.slug)) map.set(tpl.slug, tpl.name);
    }
    return Array.from(map.entries()).map(([slug, name]) => ({ slug, name }));
  }, [baseTemplates]);

  const pickVariant = (slug: string, lang: Lang): EmailTemplateBase | undefined => {
    const all = (baseTemplates ?? []).filter((tp) => tp.slug === slug);
    for (const locale of LANG_TO_LOCALE[lang]) {
      const hit = all.find((tp) => tp.language === locale);
      if (hit) return hit;
    }
    return all[0];
  };

  const applyTemplate = (slug: string) => {
    setSelectedSlug(slug);
    const en = pickVariant(slug, 'en');
    const pt = pickVariant(slug, 'pt');
    setSubjectEn(en?.subject ?? '');
    setBodyEn(en?.content ?? '');
    setSubjectPt(pt?.subject ?? '');
    setBodyPt(pt?.content ?? '');
  };

  const payloadValid =
    subjectEn.trim() && subjectPt.trim() && bodyEn.trim() && bodyPt.trim();

  const testMutation = useMutation({
    mutationFn: () =>
      EmailSenderService.send({
        mode: 'test',
        testEmail,
        subjectEn,
        subjectPt,
        bodyEn,
        bodyPt,
      }),
    onSuccess: (r) => {
      toast({
        title: t('settings.emailSender.testSent', {
          defaultValue: 'Test emails dispatched',
        }),
        description: r.message,
      });
    },
    onError: (e: unknown) => {
      toast({
        title: t('common.error'),
        description: resolveApiErrorMessage(e, t),
        variant: 'destructive',
      });
    },
  });

  const bulkMutation = useMutation({
    mutationFn: () =>
      EmailSenderService.send({
        mode: 'all-users',
        confirm: true,
        subjectEn,
        subjectPt,
        bodyEn,
        bodyPt,
      }),
    onSuccess: (r) => {
      setConfirmOpen(false);
      toast({
        title: t('settings.emailSender.bulkStarted', {
          defaultValue: 'Bulk send started',
        }),
        description: r.message,
      });
    },
    onError: (e: unknown) => {
      setConfirmOpen(false);
      toast({
        title: t('common.error'),
        description: resolveApiErrorMessage(e, t),
        variant: 'destructive',
      });
    },
  });

  return (
    <Card className="w-full">
      <CardContent className="pt-6">
        <div className="grid gap-6 lg:grid-cols-[1fr_280px]">
          {/* ---- Compose ---- */}
          <div className="space-y-4">
            {/* Content source */}
            <div className="flex items-center justify-between rounded-md border p-3">
              <div className="space-y-1">
                <Label htmlFor="use_template">
                  {t('settings.emailSender.useTemplate', { defaultValue: 'Use a template' })}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {t('settings.emailSender.useTemplateDescription', {
                    defaultValue:
                      'Load a base template into the editor, or compose the email from scratch.',
                  })}
                </p>
              </div>
              <Switch
                id="use_template"
                checked={useTemplate}
                onCheckedChange={setUseTemplate}
              />
            </div>

            {useTemplate && (
              <div className="space-y-1">
                <Label>
                  {t('settings.emailSender.template', { defaultValue: 'Template' })}
                </Label>
                <Select
                  value={selectedSlug}
                  onValueChange={applyTemplate}
                  disabled={isLoading}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t('settings.emailSender.selectTemplate', {
                        defaultValue: 'Select a template…',
                      })}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {slugOptions.map((o) => (
                      <SelectItem key={o.slug} value={o.slug}>
                        {o.name} ({o.slug})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Bilingual editor */}
            <Tabs value={activeLang} onValueChange={(v) => setActiveLang(v as Lang)}>
              <TabsList>
                <TabsTrigger value="en">EN</TabsTrigger>
                <TabsTrigger value="pt">PT</TabsTrigger>
              </TabsList>

              {(['en', 'pt'] as Lang[]).map((lng) => (
                <TabsContent key={lng} value={lng} className="space-y-3">
                  <div className="space-y-1">
                    <Label>
                      {t('settings.emailSender.subject', { defaultValue: 'Subject' })}
                    </Label>
                    <Input
                      value={lng === 'en' ? subjectEn : subjectPt}
                      onChange={(e) =>
                        lng === 'en'
                          ? setSubjectEn(e.target.value)
                          : setSubjectPt(e.target.value)
                      }
                      placeholder={lng === 'en' ? 'Subject (EN)' : 'Assunto (PT)'}
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setView('code')}
                      className={`rounded px-2.5 py-1 text-xs transition-colors ${
                        view === 'code'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      HTML
                    </button>
                    <button
                      type="button"
                      onClick={() => setView('preview')}
                      className={`rounded px-2.5 py-1 text-xs transition-colors ${
                        view === 'preview'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {t('settings.emailSender.preview', { defaultValue: 'Preview' })}
                    </button>
                  </div>

                  <div style={{ height: '480px' }} className="rounded border">
                    {view === 'code' ? (
                      <Editor
                        height="480px"
                        language="html"
                        value={lng === 'en' ? bodyEn : bodyPt}
                        onChange={(val) =>
                          lng === 'en' ? setBodyEn(val ?? '') : setBodyPt(val ?? '')
                        }
                        theme={theme === 'dark' ? 'vs-dark' : 'vs'}
                        options={{
                          minimap: { enabled: false },
                          wordWrap: 'on',
                          fontSize: 13,
                          lineHeight: 20,
                        }}
                      />
                    ) : (
                      <iframe
                        srcDoc={previewHtml(lng === 'en' ? bodyEn : bodyPt)}
                        title={`preview-${lng}`}
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        sandbox="allow-same-origin"
                      />
                    )}
                  </div>
                </TabsContent>
              ))}
            </Tabs>

            <p className="text-xs text-muted-foreground">
              {t('settings.emailSender.placeholderHint', {
                defaultValue:
                  'Only {{userName}} is replaced per recipient at send time. Fill any other {{placeholders}} before sending.',
              })}
            </p>
          </div>

          {/* ---- Send options ---- */}
          <div className="space-y-4">
            <div className="rounded-md border p-3 space-y-3">
              <Label>
                {t('settings.emailSender.testEmailTitle', { defaultValue: 'Test email' })}
              </Label>
              <p className="text-xs text-muted-foreground">
                {t('settings.emailSender.testEmailDescription', {
                  defaultValue:
                    'Sends BOTH the EN and PT versions to this address so you can review them.',
                })}
              </p>
              <Input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="you@example.com"
              />
              <Button
                type="button"
                className="w-full"
                disabled={
                  !payloadValid ||
                  !testEmail.includes('@') ||
                  testMutation.isPending
                }
                onClick={() => testMutation.mutate()}
              >
                {testMutation.isPending
                  ? t('common.sending', { defaultValue: 'Sending…' })
                  : t('settings.emailSender.sendTest', { defaultValue: 'Send test' })}
              </Button>
            </div>

            <div className="rounded-md border border-destructive/40 p-3 space-y-3">
              <Label>
                {t('settings.emailSender.allUsersTitle', {
                  defaultValue: 'Database users',
                })}
              </Label>
              <p className="text-xs text-muted-foreground">
                {t('settings.emailSender.allUsersDescription', {
                  defaultValue:
                    'Sends to every registered user, picking the EN or PT version based on their profile language. Runs in the background.',
                })}
              </p>
              <Button
                type="button"
                variant="destructive"
                className="w-full"
                disabled={!payloadValid || bulkMutation.isPending}
                onClick={() => setConfirmOpen(true)}
              >
                {t('settings.emailSender.sendAll', {
                  defaultValue: 'Send to all users',
                })}
              </Button>
            </div>
          </div>
        </div>

        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t('settings.emailSender.confirmTitle', {
                  defaultValue: 'Send to ALL registered users?',
                })}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t('settings.emailSender.confirmDescription', {
                  defaultValue:
                    'This will email every registered user in the database. This action cannot be undone. The send runs in the background.',
                })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  bulkMutation.mutate();
                }}
                disabled={bulkMutation.isPending}
              >
                {bulkMutation.isPending
                  ? t('common.sending', { defaultValue: 'Sending…' })
                  : t('settings.emailSender.confirmSend', {
                      defaultValue: 'Yes, send to all',
                    })}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
};

export default EmailSenderSettings;
